import { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  AccessGrant,
  Door,
  Mutation,
  MutationDeleteLocationArgs,
  MutationUnassignFromAccessGrantsArgs,
  MutationUpdateLocationArgs,
  Query,
  VisitorGroup
} from 'graphql/generated/graphqlTypes';
import {
  ASSIGN_DEVICE_GROUP_TO_ACCESS_GRANTS,
  DELETE_DEVICE_GROUP,
  UNASSIGN_DEVICE_GROUP_FROM_ACCESS_GRANTS,
  UPDATE_DEVICE_GROUP
} from 'graphql/v2/mutations';
import {
  GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS,
  GET_DOORS_FOR_TENANT,
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect } from 'react';
import { getAllAccessGrantsOfVisitorGroup } from 'utils/VisitorGroup/VisitorGroup';
import { resolveLocationDoors } from 'v2/utils/lock';

export type UserGroupRow = VisitorGroup & {
  accessGrant: AccessGrant | undefined;
  isEnabled: boolean;
};

interface UseDeviceGroupDetailsProps extends BaseHookProps {
  groupId: string;
}

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
// Only the door (device) ids are selected for the locations (device groups), so the doors of the group are resolved
// from the doors query.
const GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getLocationsForTenant'>> =
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS;
const GET_DOORS_FOR_TENANT_TYPED: TypedDocumentNode<Pick<Query, 'getDoorsForTenant'>> = GET_DOORS_FOR_TENANT;
// Only ids are selected for the members and access grant locations/doors, so read `memberIds` and `locationIds`
// from these groups instead of `members` and `locations`.
const GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorGroupsForTenant'>
> = GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS;
const DELETE_DEVICE_GROUP_TYPED: TypedDocumentNode<
  Pick<Mutation, 'deleteLocation'>,
  MutationDeleteLocationArgs
> = DELETE_DEVICE_GROUP;
const UNASSIGN_DEVICE_GROUP_FROM_ACCESS_GRANTS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'unassignFromAccessGrants'>,
  MutationUnassignFromAccessGrantsArgs
> = UNASSIGN_DEVICE_GROUP_FROM_ACCESS_GRANTS;
const UPDATE_DEVICE_GROUP_TYPED: TypedDocumentNode<
  Pick<Mutation, 'updateLocation'>,
  MutationUpdateLocationArgs
> = UPDATE_DEVICE_GROUP;

const useDeviceGroupDetails = ({ groupId, handleFetchError }: UseDeviceGroupDetailsProps) => {
  const { translation } = useTranslation();
  // Apollo 4 notifies on network status changes by default, which flips `loading` to true on every refetch or
  // poll. Keep it off so a refetch does not blank content that is already on screen.
  const {
    loading: locationsLoading,
    error: locationsError,
    data: locationsData,
    refetch: refetchLocations
  } = useQuery(GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED, { notifyOnNetworkStatusChange: false });

  // The doors (devices) are refetched after saving the devices of the group. Like the locations query above, keep
  // `loading` false during that refetch: the page keeps showing the current devices instead of the page loader,
  // and updates when the new data arrives.
  const {
    loading: doorsLoading,
    error: doorsError,
    data: doorsData,
    refetch: refetchDoors
  } = useQuery(GET_DOORS_FOR_TENANT_TYPED, { notifyOnNetworkStatusChange: false });

  const {
    loading: visitorGroupsLoading,
    error: visitorGroupsError,
    data: visitorGroupsData
  } = useQuery(GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED);

  useEffect(() => {
    if (locationsError) {
      handleFetchError(translation.device_group_error_fetching_details);
      console.error(locationsError);
    }
  }, [locationsError]);

  useEffect(() => {
    if (visitorGroupsError) {
      handleFetchError(translation.device_group_error_fetching_user_groups);
      console.error(visitorGroupsError);
    }
  }, [visitorGroupsError]);

  useEffect(() => {
    if (doorsError) {
      handleFetchError(translation.device_group_error_fetching_details);
      console.error(doorsError);
    }
  }, [doorsError]);

  const allLocations = locationsData?.getLocationsForTenant ?? [];
  const deviceGroup = allLocations.find((loc) => loc.id === groupId);
  const allDoors: Door[] = doorsData?.getDoorsForTenant ?? [];
  const doorsById = new Map(allDoors.map((door) => [door.id, door]));
  const doorsInGroup = deviceGroup ? resolveLocationDoors(deviceGroup, doorsById) : [];

  const allVisitorGroups = visitorGroupsData?.getVisitorGroupsForTenant ?? [];

  const userGroupsForGroup: UserGroupRow[] = allVisitorGroups.map((group) => {
    const isEnabled = getAllAccessGrantsOfVisitorGroup(group).some((grant) => grant.locationIds.includes(groupId));
    const accessGrant = group.accessGrants[0];
    return { ...group, accessGrant, isEnabled };
  });

  // The doors (devices) are refetched too, so their `locationIds` no longer hold the deleted device group.
  const [deleteLocationMutation] = useMutation(DELETE_DEVICE_GROUP_TYPED, {
    refetchQueries: [{ query: GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS }, { query: GET_DOORS_FOR_TENANT }],
    awaitRefetchQueries: true
  });

  // Both mutations return the affected AccessGrant with `id` + `locations`, not the `locationIds` this page reads,
  // so the slim groups query is refetched to keep `isEnabled` fresh.
  const [unassignFromAccessGrantsMutation] = useMutation(UNASSIGN_DEVICE_GROUP_FROM_ACCESS_GRANTS_TYPED, {
    refetchQueries: [{ query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }],
    awaitRefetchQueries: true
  });

  const [assignToAccessGrantsMutation] = useMutation(ASSIGN_DEVICE_GROUP_TO_ACCESS_GRANTS, {
    refetchQueries: [{ query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }],
    awaitRefetchQueries: true
  });

  // The mutation result updates the cached location's (device group's) name and doors (devices), so no locations
  // refetch is needed.
  const [updateDeviceGroupMutation] = useMutation(UPDATE_DEVICE_GROUP_TYPED);

  const deleteDeviceGroup = async (): Promise<{ success: boolean; errorMessage?: string }> => {
    try {
      await deleteLocationMutation({ variables: { id: groupId } });
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : translation.an_error_occurred;
      return { success: false, errorMessage };
    }
  };

  const toggleUserGroupAccess = async (userGroup: UserGroupRow, enabled: boolean): Promise<void> => {
    const allAccessGrantIds = userGroup.accessGrants.map((grant) => grant.id);
    if (allAccessGrantIds.length === 0) return;

    try {
      if (enabled) {
        await assignToAccessGrantsMutation({
          variables: { accessGrantIds: allAccessGrantIds, doorIds: [], locationIds: [groupId] }
        });
      } else {
        await unassignFromAccessGrantsMutation({
          variables: { accessGrantIds: allAccessGrantIds, doorIds: [], locationIds: [groupId] }
        });
      }
    } catch (error) {
      // The switch can only go back to its previous state, so the failure is reported here and never rethrown.
      handleFetchError(enabled ? translation.access_grant_error_adding : translation.access_grant_error_removing);
      console.error(error);
    }
  };

  const updateDeviceGroupWithDoors = async (
    doorIds: string[]
  ): Promise<{ success: boolean; errorMessage?: string }> => {
    if (!deviceGroup) return { success: false, errorMessage: translation.device_group_error_updating_devices };
    try {
      // Only a change of doors (devices) affects their `locationIds` (device groups), so the doors are refetched here
      // and not on rename.
      await updateDeviceGroupMutation({
        variables: { id: groupId, location: { name: deviceGroup.name, doorIds } },
        refetchQueries: [{ query: GET_DOORS_FOR_TENANT }],
        awaitRefetchQueries: true
      });
      return { success: true };
    } catch (err) {
      return {
        success: false,
        errorMessage: err instanceof Error ? err.message : translation.device_group_error_updating_devices
      };
    }
  };

  const renameDeviceGroup = async (name: string): Promise<{ success: boolean; errorMessage?: string }> => {
    if (!deviceGroup) return { success: false, errorMessage: translation.device_group_error_updating_name };
    try {
      await updateDeviceGroupMutation({
        variables: {
          id: groupId,
          location: { name, doorIds: doorsInGroup.map((door) => door.id) }
        }
      });
      return { success: true };
    } catch (err) {
      return {
        success: false,
        errorMessage: err instanceof Error ? err.message : translation.device_group_error_updating_name
      };
    }
  };

  return {
    deviceGroup,
    doorsInGroup,
    allDoors,
    userGroupsForGroup,
    loading: locationsLoading || visitorGroupsLoading || doorsLoading,
    // The doors (devices) of the device group come from both queries, so a failure of either one shows the error state.
    hasDeviceGroupFetchError: Boolean(locationsError || doorsError),
    // A failed retry is already reported through the query errors, so the rejection is only swallowed here.
    retryFetchingDeviceGroup: () => Promise.all([refetchLocations(), refetchDoors()]).catch(() => undefined),
    deleteDeviceGroup,
    toggleUserGroupAccess,
    updateDeviceGroupWithDoors,
    renameDeviceGroup
  };
};

export default useDeviceGroupDetails;
